/**
 * GlossaryModal Component
 *
 * A searchable glossary modal showing business term definitions
 */

import React, { useState, useMemo } from 'react';
import { X, Search, BookText, Tag, ChevronDown, ChevronRight } from 'lucide-react';
import { useGlossary } from '../hooks/useGlossary';
import { cn } from '../../../lib/utils';

const CATEGORY_LABELS: Record<string, string> = {
  quality: 'Quality',
  delivery: 'Delivery',
  purchasing: 'Sales Orders',
  general: 'General',
  finance: 'Finance',
  compliance: 'Compliance',
};

const CATEGORY_COLORS: Record<string, string> = {
  quality: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  delivery: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  purchasing: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  general: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  finance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  compliance: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

export const GlossaryModal: React.FC = () => {
  const { isOpen, close, terms, termsByCategory: _termsByCategory, isLoading, searchTerm } = useGlossary();
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);

  // Filter terms based on search
  const filteredTerms = useMemo(() => {
    if (!searchInput) return terms;
    const query = searchInput.toLowerCase();
    return terms.filter(term =>
      term.term.toLowerCase().includes(query) ||
      term.abbreviation?.toLowerCase().includes(query) ||
      term.definition.toLowerCase().includes(query)
    );
  }, [terms, searchInput]);

  // Group filtered terms by category
  const filteredByCategory = useMemo(() => {
    return filteredTerms.reduce((acc, term) => {
      if (!acc[term.category]) {
        acc[term.category] = [];
      }
      acc[term.category].push(term);
      return acc;
    }, {} as Record<string, typeof terms>);
  }, [filteredTerms]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleTermClick = (term: string) => {
    setSelectedTerm(selectedTerm === term ? null : term);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={close}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={cn(
            'w-full max-w-2xl max-h-[80vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl',
            'flex flex-col overflow-hidden'
          )}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <BookText className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Glossary
              </h2>
            </div>
            <button
              onClick={close}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close glossary"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Search */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search terms..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className={cn(
                  'w-full pl-10 pr-4 py-2 rounded-lg',
                  'border border-gray-300 dark:border-gray-600',
                  'bg-white dark:bg-gray-800',
                  'text-gray-900 dark:text-white',
                  'placeholder-gray-400 dark:placeholder-gray-500',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
                )}
                autoFocus
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : filteredTerms.length === 0 ? (
              <div className="text-center py-12">
                <BookText className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchInput ? 'No terms found matching your search.' : 'No glossary terms available.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(filteredByCategory).map(([category, categoryTerms]) => (
                  <div
                    key={category}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-3',
                        'bg-gray-50 dark:bg-gray-800',
                        'hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {CATEGORY_LABELS[category] || category}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({categoryTerms.length})
                        </span>
                      </div>
                      {expandedCategories.has(category) ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>

                    {/* Terms List */}
                    {(expandedCategories.has(category) || searchInput) && (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {categoryTerms.map(term => (
                          <div
                            key={term.id}
                            className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                          >
                            <button
                              onClick={() => handleTermClick(term.term)}
                              className="w-full text-left"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-900 dark:text-white">
                                      {term.term}
                                    </span>
                                    {term.abbreviation && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                                        {term.abbreviation}
                                      </span>
                                    )}
                                  </div>
                                  <p className={cn(
                                    'text-sm text-gray-600 dark:text-gray-300',
                                    selectedTerm === term.term ? '' : 'line-clamp-2'
                                  )}>
                                    {term.definition}
                                  </p>
                                </div>
                                <span className={cn(
                                  'px-2 py-1 rounded text-xs font-medium whitespace-nowrap',
                                  CATEGORY_COLORS[term.category] || CATEGORY_COLORS['general']
                                )}>
                                  {CATEGORY_LABELS[term.category] || term.category}
                                </span>
                              </div>

                              {/* Related Terms */}
                              {selectedTerm === term.term && term.related_terms && term.related_terms.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Related:{' '}
                                    {term.related_terms.map((related, idx) => (
                                      <span key={related}>
                                        <span className="text-primary hover:underline cursor-pointer">
                                          {related}
                                        </span>
                                        {idx < term.related_terms.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </span>
                                </div>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
